import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import {
  ChangeSampleKitAction,
  GetSampleAction,
} from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import { ExtendedSample } from '@app/modules/service-data/types/sample.types';
import { AnalysisType, Kit, KitTypes } from '@core/core.types';
import { LoadPartnerFreeKitsAction } from '@core/store/kits/kits.actions';
import { KitsState } from '@core/store/kits/kits.state';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { NEG_SINGLE_CARRIER_ANALYSES } from '@shared/constants/kit.constants';
import { ConfirmCloseReason } from '@shared/shared.types';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-sample-kit-change-dialog',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TranslateModule,
    SelectComponent,
    SelectOptionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './sample-kit-change-dialog.component.html',
})
export class SampleKitChangeDialogComponent implements OnInit {
  private store = inject(Store);
  private modalRef = inject(NgbActiveModal);
  private confirmService = inject(ConfirmService);
  private sampleUtilsService = inject(SampleUtilsService);

  @Input() sampleId: string;

  sample$: Observable<ExtendedSample>;
  protected kits$: Observable<Kit[]>;
  loading = false;

  kitFormControl = new FormControl(null, Validators.required);

  ngOnInit() {
    this.sample$ = this.store.dispatch(new GetSampleAction(this.sampleId)).pipe(
      map(() =>
        this.store.selectSnapshot(SampleState.getSampleById)(this.sampleId)
      ),
      switchMap((sample) =>
        this.sampleUtilsService.getExtendedSample(sample, { product: true })
      ),
      first()
    );

    this.kits$ = this.sample$.pipe(
      switchMap((sample) =>
        this.store
          .dispatch(new LoadPartnerFreeKitsAction(sample.partnerId))
          .pipe(
            map(() => sample.partnerId),
            switchMap((partnerId: string) =>
              this.store
                .select(KitsState.getFreePartnerKits)
                .pipe(
                  map((getFreePartnerKits) => getFreePartnerKits(partnerId))
                )
            ),
            map((kits) => {
              if (sample.product.analysisType === AnalysisType.CANCERSCREEN) {
                return kits.filter(
                  (kit) => kit.type === KitTypes.GeneralCancerScreen
                );
              } else if (
                sample.product.analysisType === AnalysisType.VENISAFE
              ) {
                return kits.filter(
                  (kit) => kit.type === KitTypes.GeneralVenisafe
                );
              } else if (sample.product.analysisType === AnalysisType.GALLERI) {
                return kits.filter(
                  (kit) => kit.type === KitTypes.GeneralGalleri
                );
              } else if (sample.product.analysisType === AnalysisType.NIPPT) {
                return kits.filter(
                  (kit) =>
                    kit.type === KitTypes.NipptM ||
                    kit.type === KitTypes.NipptAf
                );
              } else if (
                sample.product.analysisType === AnalysisType.PATERNITY
              ) {
                return kits.filter(
                  (kit) =>
                    kit.type === KitTypes.Paternity1 ||
                    kit.type === KitTypes.Paternity2
                );
              } else if (
                NEG_SINGLE_CARRIER_ANALYSES.includes(
                  sample.product.analysisType
                )
              ) {
                return kits.filter((kit) => kit.type === KitTypes.EdtaNeg);
              } else {
                return kits.filter(
                  (kit) =>
                    kit.type !== KitTypes.GeneralCancerScreen &&
                    kit.type !== KitTypes.GeneralVenisafe &&
                    kit.type !== KitTypes.GeneralGalleri &&
                    kit.type !== KitTypes.NipptM &&
                    kit.type !== KitTypes.NipptAf &&
                    kit.type !== KitTypes.Paternity1 &&
                    kit.type !== KitTypes.Paternity2 &&
                    kit.type !== KitTypes.EdtaNeg
                );
              }
            })
          )
      )
    );
  }

  submit() {
    const oldSample = this.store.selectSnapshot(SampleState.getSampleById)(
      this.sampleId
    );
    this.confirmService
      .confirm({
        title: 'Replacing kit',
        message: `Are you sure you want to switch kit ${oldSample.kitId} with ${this.kitFormControl.value}?`,
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.loading = true;
          this.store
            .dispatch(
              new ChangeSampleKitAction(
                this.sampleId,
                this.kitFormControl.value
              )
            )
            .subscribe(() => {
              this.modalRef.close();
            });
        }
      });
  }

  dismiss() {
    this.modalRef.close();
  }
}
